/**
 * CMS copy, made safe for vue-i18n (RM-420, 10/22).
 *
 * CMS rows (Admin → CMS) are merged over en.json at runtime and read through
 * vue-i18n, whose message syntax gives `@`, `{` and `}` meanings. On 10/21 a
 * bare "@" ("…email it to preferred@reserverm.com") threw "Invalid linked
 * format" and the Booked? sheet crashed as it opened. en.json is checked in
 * CI (plugins/i18n/messages.spec.js); CMS rows are edited in Admin, where no
 * test can see them. So every CMS value is handled here, as it enters
 * vue-i18n, rather than refused when saved:
 *
 *   - a bare "@" (an email address, "@ the bar") is a literal "@";
 *     a linked message ("@:key", "@.lower:key") is kept as written;
 *   - a placeholder ("{name}", "{0}", "{'@'}") is kept, so the app's own
 *     fill-ins still work (a word in braces is always read as one: copy
 *     can't show a literal "{word}");
 *   - any other "{" or "}" (a typo, an unclosed brace) is a literal.
 *
 * The result always compiles and reads exactly as the editor typed it.
 * Non-string values (blocks, arrays) pass through untouched.
 */

import { escapeHtml } from './agentMessageHtml'

// What vue-i18n reads as a placeholder, at the start of the string: a named
// one ({name}, {user.firstName}), a list one ({0}) or a literal ({'@'}).
const PLACEHOLDER = /^\{\s*(?:[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*|\d+|'(?:[^'\\]|\\.)*')\s*\}/

// A linked message: @:key or @.modifier:key
const LINKED = /^@(?:\.[a-z]+)?:[A-Za-z_$][\w$.-]*/

/**
 * @param {*} value - a CMS value
 * @returns {*} the same text, safe for vue-i18n (non-strings unchanged)
 */
export function safeCmsMessage(value) {
  if (typeof value !== 'string' || !/[@{}]/.test(value))
    return value

  let out = ''
  let i = 0
  while (i < value.length) {
    const rest = value.slice(i)
    const ch = value[i]
    if (ch === '{') {
      const m = PLACEHOLDER.exec(rest)
      if (m) {
        out += m[0]
        i += m[0].length
        continue
      }
      out += '{\'{\'}'
    }
    else if (ch === '}') {
      out += '{\'}\'}'
    }
    else if (ch === '@') {
      const m = LINKED.exec(rest)
      if (m) {
        out += m[0]
        i += m[0].length
        continue
      }
      out += '{\'@\'}'
    }
    else {
      out += ch
    }
    i += 1
  }

  return out
}

/**
 * A flat CMS map ({ 'dot.key': value }), with every value made safe for vue-i18n.
 * @param {Record<string, *>} flatMap
 * @returns {Record<string, *>}
 */
export function cmsMessagesForI18n(flatMap) {
  const out = {}
  for (const [key, value] of Object.entries(flatMap || {}))
    out[key] = safeCmsMessage(value)

  return out
}

/**
 * Fill a CmsText line's placeholders ({restaurant}) with its values (RM-420 comment 10, 10/22).
 *
 * A line CMS marks as rich is rendered as HTML, and the values are data a member can set (a
 * restaurant's name is whatever the member who added it typed), so in a rich line each value is
 * HTML-escaped; a plain line is rendered as text and keeps the value as written ("Girl & the
 * Goat"). Whether a line is rich is decided from the copy itself, before the values go in, so a
 * value can never turn a plain line into HTML. Unknown placeholders are left as they are.
 */
export function fillCmsVars(template, vars, { html = false } = {}) {
  if (typeof template !== 'string' || !vars)
    return template

  return template.replace(/\{(\w+)\}/g, (match, name) => {
    if (!Object.prototype.hasOwnProperty.call(vars, name) || vars[name] == null)
      return match
    const value = String(vars[name])

    return html ? escapeHtml(value) : value
  })
}
